import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map } from 'lodash';
import Vue from 'vue';

import WishListApi from '@/extensions/shop/store/api/wishList';
import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

// Multistore: the store the admin UI currently has selected. Lists of store-owned
// records default to it, so a satellite stops showing its siblings' rows; the
// store filter widens them again.
const selectedStoreId = () => _.get(useSetupStore(pinia), 'activeCms.id', null);

const defaultForm = {
    id: undefined
};

const defaultRules = {};

const defaultUi = {
    form: {
        initialized: false,
        validateOnEnter: false
    },
    articlesPaginated: {
        loading: true
    }
};

export const useShopWishListStore = defineStore('shopWishList', {
    state: () => ({
        loading: true,
        wishListsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        articlesPaginated: {
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {},
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => get(state, 'ui.form.initialized'),
        formValidateOnEnter: (state) => get(state, 'ui.form.validateOnEnter')
    },

    actions: {
        addPaginatedWishListsToState(data) {
            this.wishListsPaginated.items = data.items;
            this.wishListsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.wishListsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.wishListsPaginated.link = cloneDeep(data.config.link);
            this.wishListsPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.wishListsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.wishListsPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.wishListsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.wishListsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.wishListsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.wishListsPaginated.filters, key);
            } else {
                Vue.set(this.wishListsPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.wishListsPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.wishListsPaginated.variables, 'order', key);
            Vue.set(this.wishListsPaginated.variables, 'direction', value);
        },

        // Wish list articles
        addPaginatedArticlesToState(data) {
            this.articlesPaginated.items = data.items;
            // Preserve selection when updating pageInfo
            const currentSelection = cloneDeep(this.articlesPaginated.pageInfo.selection);
            this.articlesPaginated.pageInfo = cloneDeep(data.pageInfo);
            Vue.set(this.articlesPaginated.pageInfo, 'selection', currentSelection || { type: 'inclusive', identifiers: [], total: 0 });
            this.articlesPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.articlesPaginated.link = cloneDeep(data.config.link);
            this.articlesPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.articlesPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineArticlesActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.articlesPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.articlesPaginated.activeColumns = activeColumns;
            }
        },

        defineArticlesPerPage(perPage) {
            this.articlesPaginated.pageInfo.perPage = perPage;
        },

        defineArticlesCurrentPage(currentPage) {
            this.articlesPaginated.pageInfo.currentPage = currentPage;
        },

        defineArticlesFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.articlesPaginated.filters, key);
            } else {
                Vue.set(this.articlesPaginated.filters, key, value);
            }
        },

        defineArticlesFilterVisibility(value) {
            Vue.set(this.articlesPaginated, 'showFilters', value);
        },

        defineArticlesSorting({ key, value }) {
            Vue.set(this.articlesPaginated.variables, 'order', key);
            Vue.set(this.articlesPaginated.variables, 'direction', value);
        },

        defineArticlesPaginationSizes(value) {
            Vue.set(this.articlesPaginated.variables, 'paginationSizes', value);
        },

        updateArticlesPaginationLoading(value) {
            this.ui.articlesPaginated.loading = value;
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            Vue.set(this, 'rules', cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        insertWishList(wishList) {
            Vue.set(this, 'form', wishList);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormLatitude({ i, value }) {
            Vue.set(this.form.customer.member.addresses[i], 'latitude', value);
        },

        setFormLongitude({ i, value }) {
            Vue.set(this.form.customer.member.addresses[i], 'longitude', value);
        },

        // Async actions
        fetchWishListsPaginated() {
            return WishListApi.fetchWishListsPaginated({ cms: selectedStoreId(), ...this.wishListsPaginated }).then(data => {
                this.addPaginatedWishListsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchArticlesPaginated(payload = {}) {
            return WishListApi.fetchWishListArticlesPaginated({ ...payload, ...this.articlesPaginated }, this.form.id).then(data => {
                this.addPaginatedArticlesToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineArticlesActiveColumns(activeColumnKeys);
            });
        },

        fetchWishList(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            this.articlesPaginationBulkSelectionReset();

            return WishListApi.fetchWishList(id, cms.id).then(wishList => {
                this.insertWishList(cloneDeep(wishList));
            });
        },

        reset() {
            this.insertWishList(cloneDeep(defaultForm));
            this.articlesPaginationBulkSelectionReset();
        },

        initializeWishListImport({ shopID, file }) {
            return WishListApi.initializeImport(shopID, file);
        },

        validateWishListImportCsv({ shopID, file }) {
            return WishListApi.validateImportCsv(shopID, file);
        },

        // Bulk selection actions for articles
        articlesPaginationBulkSelectionCheckAll({ value }) {
            let selection = this.articlesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.articlesPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        articlesPaginationBulkSelectionCheckVisible({ value }) {
            const items = get(this.articlesPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.articlesPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        articlesPaginationBulkSelectionCheckItem(item) {
            let selection = this.articlesPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        articlesPaginationBulkSelectionReset() {
            Vue.set(this.articlesPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        processArticlesPaginationBulkAction(action, wishListId) {
            return WishListApi.processWishListArticlePaginationBulkAction(action, this.articlesPaginated, wishListId);
        },
    }
});
